import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
  createEditor,
  Editor,
  Node,
  Transforms,
  Descendant,
  NodeEntry,
  Text,
  Element,
} from "slate";
import {Slate, Editable, withReact} from "slate-react";
import {BaseEditor} from "slate";
import {ReactEditor} from "slate-react";
import {PromptDropdown} from "./PromptDropdown";
import {PromptVariable, VariableTypes} from "@/types/node";
import {VariableParams} from "@/types/general";
import {cn} from "@/lib/utils";
import {motion} from "framer-motion";

type CustomElement = {
  type: "paragraph" | "variable";
  children: any[];
  open?: string;
  close?: string;
};
type CustomText = {text: string; highlight?: boolean};

declare module "slate" {
  interface CustomTypes {
    Editor: BaseEditor & ReactEditor;
    Element: CustomElement;
    Text: CustomText;
  }
}

const serialize = nodes => {
  return nodes
    .map(topLevelNode => {
      if (topLevelNode.type === "variable") {
        // If the top-level node itself is a variable
        return `${topLevelNode.open}${Node.string(topLevelNode)}${topLevelNode.close}`;
      } else if (topLevelNode.children) {
        // Serialize child nodes
        return topLevelNode.children
          .map(child => {
            if (child.type === "variable") {
              return `${child.open}${Node.string(child)}${child.close}`;
            }
            return Node.string(child);
          })
          .join("");
      }
      // Fallback for nodes without .children
      return Node.string(topLevelNode);
    })
    .join("\n"); // Use newlines to separate paragraphs
};

const getCaretTopPosition = editor => {
  const {selection} = editor;
  if (!selection) return null;

  const domSelection = window.__flowBuilderIframeDoc.getSelection();
  if (!domSelection || domSelection.rangeCount === 0) return null;

  const range = domSelection.getRangeAt(0);
  const rects = range.getClientRects();
  if (rects.length === 0) return null;

  const caretRect = rects[0]; // Caret position
  const editorNode = ReactEditor.toDOMNode(editor, editor);
  const editorRect = editorNode.getBoundingClientRect();

  return caretRect.top - editorRect.top + caretRect.height + 8; // Caret position relative to editor
};

const cleanSearchParam = (value: string, variableParams: VariableParams) => {
  let cleanedValue = value.replace(/\u200B/g, "");
  Object.values(variableParams).forEach(
    ({open, close}: {open: string; close: string}) => {
      if (open)
        cleanedValue = cleanedValue.replace(new RegExp(`\\${open}`, "g"), "");
      if (close)
        cleanedValue = cleanedValue.replace(new RegExp(`\\${close}`, "g"), "");
    },
  );
  return cleanedValue;
};

const useDecorate = (editor, variableParams) => {
  return useCallback(
    ([node, path]: NodeEntry) => {
      if (!Element.isElement(node)) return [];
      const ranges = [];
      const text = Node.string(node);
      const regex = new RegExp(
        Object.values(variableParams as VariableParams)
          .map(({open, close}) =>
            close
              ? `\\${open}[^\\${open}\\${close}]*\\${close}`
              : `\\${open}[^\\${open}]*\\${open}`,
          )
          .join("|"),
        "g",
      );

      let match;

      while ((match = regex.exec(text))) {
        const matchedText = match[0];
        const start = match.index;
        const end = start + matchedText.length;
        // Identify the variable type
        const selectedType = Object.keys(variableParams).find(
          type =>
            matchedText.startsWith(variableParams[type].open) &&
            (!variableParams[type].close ||
              matchedText.endsWith(variableParams[type].close)),
        );

        const open = variableParams[selectedType].open;
        const close = variableParams[selectedType].close;

        const openLength = open.length;
        const closeLength = close ? close.length : 0;
        const innerStart = start + openLength;
        const innerEnd = end - closeLength;

        const variableId = `${selectedType}-${path.join("-")}-${start}`;

        // Push separate ranges
        if (openLength > 0) {
          ranges.push({
            anchor: {path, offset: start},
            focus: {path, offset: start + openLength},
            highlight: true,
            type: selectedType,
            segment: "open",
            variableId,
          });
        }

        if (innerEnd > innerStart) {
          ranges.push({
            anchor: {path, offset: innerStart},
            focus: {path, offset: innerEnd},
            highlight: true,
            type: selectedType,
            segment: "content",
            variableId,
          });
        }

        if (closeLength > 0) {
          ranges.push({
            anchor: {path, offset: end - closeLength},
            focus: {path, offset: end},
            highlight: true,
            type: selectedType,
            segment: "close",
            variableId:
              selectedType === VariableTypes.CUSTOM_ACTIONS ? "" : variableId,
          });
        }
      }

      return ranges;
    },
    [variableParams],
  );
};

interface SlatePromptEditorProps {
  value: string;
  placeholder?: string;
  onChange: (newValue: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  getVariablesData?: (
    searchParam: string,
    type: VariableTypes,
  ) => PromptVariable[];
  variableParams?: VariableParams;
  hideLegend?: boolean;
  disabled?: boolean;
  textareaClassName?: string;
}

const SlatePromptEditor: React.FC<SlatePromptEditorProps> = ({
  value,
  placeholder,
  onChange,
  onFocus,
  onBlur,
  getVariablesData,
  variableParams,
  hideLegend = false,
  disabled = false,
  textareaClassName,
}) => {
  const editor = useMemo(() => withReact(createEditor()), []);
  const [isFocused, setIsFocused] = useState(false);
  const prevValRef = useRef(value);
  const [, setForceRerenderTick] = useState(0);
  const decorate = useDecorate(editor, variableParams);
  const [lastHighlightedPath, setLastHighlightedPath] = useState(null);
  const [caretBottomPosition, setCaretBottomPosition] = useState(null);
  const [autocompleteVisible, setAutocompleteVisible] = useState(false);
  const [selectedType, setSelectedType] = useState(VariableTypes.VARIABLES);
  const [searchParam, _setSearchParam] = useState("");

  const setSearchParam = value => {
    _setSearchParam(cleanSearchParam(value, variableParams));
  };

  const initialValue = useMemo(
    () => [
      {
        type: "paragraph",
        children: [{text: value}],
      },
    ],
    [value],
  );

  const toggleAutocompleteDropdown = (
    show: boolean,
    editor: Editor,
    searchParam: string,
    leafType?: string,
  ) => {
    if (show) {
      setSearchParam(searchParam);
      setSelectedType(leafType as VariableTypes); // Set type from renderLeaf data
      setAutocompleteVisible(true); // Always true when triggered by clicking a highlighted leaf
      setCaretBottomPosition(getCaretTopPosition(editor));
    } else {
      setAutocompleteVisible(false);
    }
  };

  const handleLegendClick = (
    type: string,
    event: React.MouseEvent<HTMLDivElement>,
  ) => {
    event.preventDefault();
    ReactEditor.focus(editor);

    const openBracket = variableParams[type].open;
    const closingBracket = variableParams[type].close;
    const isAction = type === VariableTypes.CUSTOM_ACTIONS;
    const content = isAction ? "" : "\u200B";

    const textToInsert = `${openBracket}${content}${closingBracket}`;
    const {path, offset} = lastHighlightedPath.anchor;
    Transforms.insertText(editor, textToInsert, {
      at: {path, offset},
    });
    Transforms.move(editor, {
      distance: 1,
      unit: "character",
      reverse: true,
    });
    Transforms.select(editor, {
      anchor: {path, offset: offset + openBracket.length + (isAction ? 0 : 1)},
      focus: {path, offset: offset + openBracket.length + (isAction ? 0 : 1)},
    });

    toggleAutocompleteDropdown(true, editor, "", selectedType);
  };

  const renderLeaf = (props, disabled) => {
    const {attributes, children, leaf} = props;

    if (!leaf.highlight) {
      return <span {...attributes}>{children}</span>;
    }

    const handleClick = event => {
      if (leaf.highlight) {
        event.preventDefault();
        console.log(
          "Clicked on:",
          leaf.type,
          "with content:",
          children.props.text,
        );
        setLastHighlightedPath(props.leafPath); // Store the path
        toggleAutocompleteDropdown(true, editor, "", leaf.type);
      }
    };

    const leafText = children.props.leaf?.text;
    const variableContent = leafText?.slice(1, -1) || "";
    const isAction = leaf.type === VariableTypes.CUSTOM_ACTIONS;
    const isClose = leaf.segment === "close";
    const isContent = leaf.segment === "content";

    const isValidVariable =
      getVariablesData(variableContent, leaf.type as VariableTypes).length > 0;

    const segmentClassMap = {
      open: "font-semibold text-primary-accent rounded-l-md border-r-0 pl-1 ml-0.5 open",
      content: "border-x-0 px-1 break-words",
      close:
        "font-semibold text-primary-accent rounded-r-md border-l-0 pr-1 mr-0.5 close",
    };

    const baseClass =
      "whitespace-nowrap tracking-wide border bg-white h-5 leading-4 cursor-pointer flex-none inline-block";
    const segmentClass = segmentClassMap[leaf.segment];
    const actionCloseClass = `\
      ${isAction ? "-mb-1.5" : ""}\
      ${isAction && isClose ? "border-0" : ""}\
      ${isAction && isContent ? "border-r rounded-r-md pr-1" : ""}
    `;

    return (
      <span
        {...attributes}
        data-variable-id={leaf.variableId}
        className={`${baseClass} ${segmentClass} ${actionCloseClass} ${
          disabled ? "opacity-50 pointer-events-none" : ""
        }`}
        onClick={handleClick}
        spellCheck={leaf.highlight ? false : true}
      >
        {children}
      </span>
    );
  };

  const onVariableClick = (variable, type) => {
    if (!editor.selection && !lastHighlightedPath) return;

    const selection = editor.selection || lastHighlightedPath;
    const {path, offset} = selection.anchor;

    try {
      const nodeEntry = Editor.node(editor, path);
      if (!nodeEntry) return;

      const [node] = nodeEntry;

      if (Text.isText(node)) {
        const text = node.text;

        // Find the correct match that includes the current offset (cursor position)
        let match;
        Object.values(variableParams as VariableParams).some(
          (v: VariableParams[keyof VariableParams]) => {
            const {open, close} = v;
            const regex = new RegExp(
              `\\${open}([^\\${open}\\${close}]*)\\${close}`,
              "g",
            );
            let tempMatch;
            while ((tempMatch = regex.exec(text))) {
              // Ensure cursor is inside the matched brackets
              if (
                offset >= tempMatch.index &&
                offset <= tempMatch.index + tempMatch[0].length
              ) {
                match = tempMatch;
                break;
              }
            }
            return match;
          },
        );

        if (!match) return;

        const fullMatch = match[0];

        const matchedVar = Object.values(variableParams).find(
          ({open, close}) =>
            fullMatch.startsWith(open) && fullMatch.endsWith(close),
        );

        if (!matchedVar) return;
        const {open, close} = matchedVar as {open: string; close: string};

        if (!open || !close) return; // No valid bracket config found

        // Compute new text with updated content inside the brackets
        const variableText = `${open}${variable}${close}`;
        const newText =
          text.substring(0, match.index) +
          variableText +
          text.substring(match.index + fullMatch.length);
        const newOffset = match.index + variableText.length;

        // Replace text while keeping brackets intact
        Transforms.insertText(editor, newText, {at: path});

        // Move the cursor to the end of the replaced variable
        Transforms.select(editor, {
          anchor: {path, offset: newOffset},
          focus: {path, offset: newOffset},
        });

        setLastHighlightedPath(null);
      }
    } catch (error) {
      console.warn("Error updating highlighted text:", error);
    }

    setAutocompleteVisible(false);
  };

  const handleBracketAutoClose = event => {
    const openBrackets = Object.values(variableParams).map(({open}) => open);
    const closeBracketsMap = Object.fromEntries(
      Object.values(variableParams)
        .filter(({close}) => close)
        .map(({open, close}) => [open, close]),
    );

    if (openBrackets.includes(event.key)) {
      event.preventDefault();

      const selectedType = Object.keys(variableParams).find(
        type => variableParams[type].open === event.key,
      );

      const closingBracket = closeBracketsMap[event.key] || "";
      const textToInsert = `${event.key}${"\u200B"}${closingBracket}`;

      // Insert brackets and move cursor inside
      Transforms.insertText(editor, textToInsert);
      Transforms.move(editor, {distance: 1, unit: "character", reverse: true});

      // OPen autocomplete dropdown
      toggleAutocompleteDropdown(true, editor, "", selectedType);
    } else {
      setAutocompleteVisible(false);
    }
  };

  const handleAutocompleteVisibility = editor => {
    const {selection} = editor;
    if (!selection) return;

    const {path, offset} = selection.anchor; // Get cursor position
    const [node] = Editor.node(editor, path);

    if (Text.isText(node)) {
      const text = node.text;

      // Find matching highlighted text that includes the cursor
      let match;
      const matchedType = Object.keys(variableParams).find(type => {
        const {open, close} = variableParams[type];
        const regex = new RegExp(
          `\\${open}([^\\${open}\\${close}]*)\\${close}`,
          "g",
        );

        let tempMatch;
        while ((tempMatch = regex.exec(text))) {
          if (
            offset >= tempMatch.index + 1 &&
            offset <= tempMatch.index + tempMatch[0].length - 1
          ) {
            match = tempMatch;
            return true;
          }
        }
        return false;
      });

      if (match) {
        const [, insideContent] = match;
        setSearchParam(insideContent);
        setSelectedType(matchedType as VariableTypes);
        setAutocompleteVisible(true); // Ensure dropdown is always visible when inside a leaf
      } else {
        setAutocompleteVisible(false);
      }
    }
  };

  useEffect(() => {
    if (autocompleteVisible) {
      setCaretBottomPosition(getCaretTopPosition(editor));
    }
  }, [autocompleteVisible]);

  useEffect(() => {
    // Highlight the variable when hovering over it
    // also hilighting all segments with same variableId
    const handleMouseEnter = e => {
      const variableId = e.target.dataset.variableId;
      if (!variableId) return;
      document
        .querySelectorAll(`[data-variable-id="${variableId}"]`)
        .forEach(el => el.classList.add("!bg-slate-100"));
    };

    const handleMouseLeave = e => {
      const variableId = e.target.dataset.variableId;
      if (!variableId) return;
      document
        .querySelectorAll(`[data-variable-id="${variableId}"]`)
        .forEach(el => el.classList.remove("!bg-slate-100"));
    };

    const elements = document.querySelectorAll("[data-variable-id]");
    elements.forEach(el => {
      el.addEventListener("mouseenter", handleMouseEnter);
      el.addEventListener("mouseleave", handleMouseLeave);
    });

    return () => {
      elements.forEach(el => {
        el.removeEventListener("mouseenter", handleMouseEnter);
        el.removeEventListener("mouseleave", handleMouseLeave);
      });
    };
  }, []);

  useEffect(() => {
    if (!isFocused) {
      // Workarouynd to support value change from outside as Slate doesn't support it
      // Compare new value with current content
      const current = JSON.stringify(editor.children);
      const next = JSON.stringify(value);
      if (current !== next) {
        // Replace editor content when not focused
        Editor.withoutNormalizing(editor, () => {
          editor.children = initialValue as Descendant[];
          Transforms.deselect(editor);
        });
        prevValRef.current = value;
        setForceRerenderTick(t => t + 1);
      }
    }
  }, [value, isFocused]);

  return (
    <Slate
      editor={editor}
      initialValue={initialValue as Descendant[]}
      onChange={nodes => {
        const serialized = serialize(nodes);
        if (serialized !== value) {
          onChange?.(serialized);
        }
        handleAutocompleteVisibility(editor);
      }}
    >
      <div className="relative">
        <Editable
          placeholder={placeholder}
          renderPlaceholder={({attributes}) => (
            <span
              {...attributes}
              className="absolute text-sm left-0 top-0 !opacity-100 text-gray-400 pointer-events-none"
            >
              {placeholder}
            </span>
          )}
          onKeyDown={event => {
            handleBracketAutoClose(event);
            setLastHighlightedPath(editor.selection);
          }}
          onBlur={() => {
            setAutocompleteVisible(false);
            setSearchParam("");
            setIsFocused(false);
            onBlur?.();
          }}
          onFocus={() => {
            setIsFocused(true);
            onFocus?.();
          }}
          onMouseLeave={() => {
            setLastHighlightedPath(editor.selection);
          }}
          decorate={decorate}
          renderLeaf={props => renderLeaf(props, disabled)}
          readOnly={disabled}
          className={cn(
            `relative w-full px-4 py-3 !min-h-28 max-h-85 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-y overflow-auto`,
            textareaClassName,
            disabled ? "bg-muted pointer-events-none" : "",
          )}
          disabled={disabled}
        />
        {autocompleteVisible && !disabled && (
          <PromptDropdown
            selectedType={selectedType}
            searchParam={searchParam}
            promptTopPosition={caretBottomPosition}
            variableParams={variableParams}
            onVariableClick={onVariableClick}
            setAutocompleteVisible={setAutocompleteVisible}
            getVariablesData={getVariablesData}
          />
        )}
        {!hideLegend && (
          <div
            className={cn(
              "flex items-center text-label text-sm mt-2",
              Object.keys(variableParams).length === 3
                ? "justify-between"
                : "justify-start",
              disabled ? "opacity-50 pointer-events-none" : "",
            )}
          >
            {Object.keys(variableParams).map(type => (
              <div
                key={type}
                tabIndex={-1}
                onClick={e => handleLegendClick(type, e)}
                className="flex items-center cursor-pointer bg-white p-1 pr-2 rounded-md hover:bg-muted"
              >
                <span className="h-6 w-6 text-primary border rounded mr-2 shadow-sm flex items-center justify-center bg-white">
                  {variableParams[type].open}
                </span>
                {variableParams[type].name}
              </div>
            ))}
          </div>
        )}
      </div>
    </Slate>
  );
};

export default SlatePromptEditor;
